import {
  takeEvery, call, put,
} from 'redux-saga/effects';

import { requestPost, requestGet } from '../utils/request';

import {
  API_URLS, MODALS, SUCCESS_MODALS, FETCH_OPTIONS,
} from '../constants';
import { aUserAdd, aUserEditInfo, aUserEditRoles, aUserEditStatus, aUserList, aUserRemove, aUserResetPass } from '../reducers';
import { aSetIsFetching, aSetOpenModal, aSetUserList } from '../reducers/common';
import { aUserAddFailed } from '../reducers/admin';

/**
 * @namespace sagas/admin
 */
/**
 * @memberof sagas/admin
 * @typedef User
 * @type {object}
 * @property {string} email
 * @property {string} insertionDate
 * @property {string} insertionUser
 * @property {string} lastLogin
 * @property {string} name
 * @property {Array} roles
 * @property {number} status
 * @property {number} userId
 * @property {string} username
 */
/**
 * @memberof sagas/admin
 * @typedef "userList/response.data"
 * @type {object}
 * @property {Array<User>} users
 * @property {object} meta
 */
/** Fetch list of users
 *
 * @memberof sagas/admin
 * @async
 * @param {object} action Action params.
 */
function* userList(action) {
  const filters = action.payload || {};
  const params = `?${new URLSearchParams(filters).toString()}`;

  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.USER_LIST,
      value: true,
    }));

    const response = yield call(requestGet, API_URLS.USER_LIST + params, true);
    if (response.status === 1) {
      yield put(aSetUserList({
        userList: response.data,
        meta: response.data.meta,
      }));

      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.USER_LIST,
        value: false,
      }));
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.USER_LIST,
      value: false,
    }));
  }
}

/**
 * Add new user
 *
 * @memberof sagas/admin
 * @async
 * @param {object} action Action info.
 */
function* userAdd(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.USER_ADD,
      value: true,
    }));
    const response = yield call(requestPost, API_URLS.USER_ADD, {
      username: action.payload.username,
      email: action.payload.email,
      name: action.payload.name,
      roleIds: action.payload.roleIds,
    }, true);

    if (response.status === 1) {
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'Added user',
          additionalMessage: 'successfully',
          modal: SUCCESS_MODALS.ADD_USER,
        },
        key: FETCH_OPTIONS.USER_ADD,
        value: false,
      }));
      yield put(aUserList());
    } else {
      yield put(aUserAddFailed({
        error: response.error.message,
      }));
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.USER_ADD,
      value: false,
    }));
  }
}

/**
 * Remove user
 *
 * @memberof sagas/admin
 * @async
 * @param {object} action Action info.
 */
function* removeUser(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.USER_REMOVE,
      value: true,
    }));

    const response = yield call(
      requestPost,
      API_URLS.USER_REMOVE, action.payload, true,
    );
    if (response.status === 1) {
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'Removed user',
          additionalMessage: 'successfully',
          modal: SUCCESS_MODALS.REMOVE_USER,
          user: action.payload,
        },
        key: FETCH_OPTIONS.USER_REMOVE,
        value: false,
      }));

      yield put(aUserList());
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.USER_REMOVE,
      value: false,
    }));
  }
}

/**
 * Reset User Password
 *
 * @memberof sagas/admin
 * @async
 * @param {object} action Action info.
 */
function* resetPassword(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.RESET_PASS,
      value: true,
    }));
    const response = yield call(requestPost, API_URLS.USER_RESET_PASS, action.payload, true);
    if (response.status === 1) {
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'The new password for the user',
          additionalMessage: 'has been sent to',
          heading: 'Password Successfully Reset',
          email: true,
          user: action.payload,
          modal: SUCCESS_MODALS.RESET_PASSWORD,
        },
        key: FETCH_OPTIONS.RESET_PASS,
        value: false,
      }));
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.RESET_PASS,
      value: false,
    }));
  }
}

/**
 * Edit roles for user
 *
 * @memberof sagas/admin
 * @async
 * @param {object} action Action info.
 */
function* editUserRoles(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.EDIT_USER_ROLES,
      value: true,
    }));
    const response = yield call(requestPost, API_URLS.USER_EDIT_ROLES, {
      userId: action.payload.user.userId,
      roleIds: action.payload.roleIds,
    }, true);
    if (response.status === 1) {
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'Roles has been changed for user',
          modal: SUCCESS_MODALS.EDIT_ROLES,
          user: action.payload.user,
        },
        key: FETCH_OPTIONS.EDIT_USER_ROLES,
        value: false,
      }));

      yield put(aUserList());
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.EDIT_USER_ROLES,
      value: false,
    }));
  }
}

/**
 * Edit status of user
 *
 * @memberof sagas/admin
 * @async
 * @param {object} action Action info.
 */
function* editUserStatus(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.EDIT_USER_STATUS,
      value: true,
    }));
    const response = yield call(requestPost, API_URLS.USER_EDIT_STATUS, {
      userId: action.payload.userId,
      status: action.payload.status,
    }, true);
    if (response.status === 1) {
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'Status has been changed for user',
          modal: SUCCESS_MODALS.EDIT_STATUS,
          user: action.payload,
        },
        key: FETCH_OPTIONS.EDIT_USER_STATUS,
        value: false,
      }));

      yield put(aUserList());
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.EDIT_USER_STATUS,
      value: false,
    }));
  }
}

/**
 * Edit Information of user
 *
 * @memberof sagas/admin
 * @async
 * @param {object} action Action Info.
 */
function* editUserInfo(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.EDIT_USER_INFO,
      value: true,
    }));
    const response = yield call(requestPost, API_URLS.USER_EDIT_INFO, {
      userId: action.payload.user.userId,
      name: action.payload.name,
      email: action.payload.email,
    }, true);
    if (response.status === 1) {
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'Info has been changed for user',
          modal: SUCCESS_MODALS.EDIT_INFO,
          user: action.payload.user,
        },
        key: FETCH_OPTIONS.EDIT_USER_INFO,
        value: false,
      }));

      yield put(aUserList());
    } else {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.EDIT_USER_INFO,
      value: false,
    }));
  }
}

/**
 * Admin Saga
 *
 * @memberof sagas/admin
 * @async
 */
export default function* adminSaga() {
  yield takeEvery(aUserList, userList);
  yield takeEvery(aUserAdd, userAdd);
  yield takeEvery(aUserRemove, removeUser);
  yield takeEvery(aUserResetPass, resetPassword);
  yield takeEvery(aUserEditRoles, editUserRoles);
  yield takeEvery(aUserEditStatus, editUserStatus);
  yield takeEvery(aUserEditInfo, editUserInfo);
}
